/**
 * Reads a `t` (template ID) query parameter, fetches the template via
 * the agency's template list (cross-tenant safe), substitutes any matching
 * prefill values from the URL into the template content, and drops the
 * result into the chat input. Auto-sends only when the template has no
 * variables or every variable is prefilled — otherwise the user fills the
 * remaining placeholders inline in the input. Cleans up URL params after
 * consumption. Sets isPlanRestricted when the agency requires a paid plan (403).
 */

import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { useGetAgencyPromptTemplatesQuery } from '@saaga/shared/store/prompt-template-endpoints.store'
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import { parseTemplateVariables } from '@/components/organisms/template-variable-editor/template-variable-parser.util'
import { substituteTemplateVariables } from '@/components/organisms/template-variable-form/template-variable-substitutor.util'
import { extractPrefillValues } from '@/lib/deep-link-intent.util'

const TEMPLATE_PARAM = 't'
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

interface TemplateActionHandlers {
  setInputValue: (value: string) => void
  triggerPendingTemplateSend: () => void
}

interface UseChatDeepLinkParams extends TemplateActionHandlers {
  agencySlug?: string
}

export interface UseChatDeepLinkReturn {
  isPlanRestricted: boolean
}

const filterPrefillForVariables = (
  variableNames: Set<string>,
  prefillValues: Record<string, string>
): Record<string, string> => {
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(prefillValues)) {
    if (variableNames.has(key)) result[key] = value
  }
  return result
}

type QueryError = FetchBaseQueryError | SerializedError | undefined

const isForbiddenError = (error: QueryError): boolean =>
  !!error && 'status' in error && error.status === 403

const applyTemplateToChat = (
  template: PromptTemplate,
  prefillValues: Record<string, string>,
  handlers: TemplateActionHandlers
): void => {
  const variableList = parseTemplateVariables(template.content)
  if (variableList.length === 0) {
    handlers.setInputValue(template.content)
    handlers.triggerPendingTemplateSend()
    return
  }
  const variableNames = new Set(variableList.map((v) => v.name))
  const matchingPrefill = filterPrefillForVariables(variableNames, prefillValues)
  handlers.setInputValue(substituteTemplateVariables(template.content, matchingPrefill))
  const allFilled = variableList.every((v) => matchingPrefill[v.name])
  if (allFilled) handlers.triggerPendingTemplateSend()
}

const useCleanupParams = (): ((paramNames: string[]) => void) => {
  const [, setSearchParams] = useSearchParams()
  return useCallback(
    (paramNames: string[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const name of paramNames) next.delete(name)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )
}

interface ErrorEffectParams {
  isConsumedRef: MutableRefObject<boolean>
  isError: boolean
  error: QueryError
  cleanupParams: (paramNames: string[]) => void
  setIsPlanRestricted: (value: boolean) => void
}

const useFetchErrorEffect = ({
  isConsumedRef,
  isError,
  error,
  cleanupParams,
  setIsPlanRestricted,
}: ErrorEffectParams): void => {
  useEffect(() => {
    if (isConsumedRef.current || !isError) return
    isConsumedRef.current = true
    if (isForbiddenError(error)) {
      setIsPlanRestricted(true)
      cleanupParams([TEMPLATE_PARAM])
      return
    }
    toast.error('Template not found')
    cleanupParams([TEMPLATE_PARAM])
  }, [isError, error, cleanupParams, isConsumedRef, setIsPlanRestricted])
}

interface InvalidIdEffectParams {
  isConsumedRef: MutableRefObject<boolean>
  templateId: string | null
  isValidId: boolean
  cleanupParams: (paramNames: string[]) => void
}

const useInvalidIdEffect = ({
  isConsumedRef,
  templateId,
  isValidId,
  cleanupParams,
}: InvalidIdEffectParams): void => {
  useEffect(() => {
    if (!templateId || isConsumedRef.current || isValidId) return
    isConsumedRef.current = true
    cleanupParams([TEMPLATE_PARAM])
  }, [templateId, isValidId, cleanupParams, isConsumedRef])
}

interface TemplateMissingEffectParams {
  isConsumedRef: MutableRefObject<boolean>
  templateList: { itemList: PromptTemplate[] } | undefined
  templateId: string | null
  template: PromptTemplate | undefined
  cleanupParams: (paramNames: string[]) => void
}

const useTemplateMissingEffect = ({
  isConsumedRef,
  templateList,
  templateId,
  template,
  cleanupParams,
}: TemplateMissingEffectParams): void => {
  useEffect(() => {
    if (isConsumedRef.current || !templateList || !templateId || template !== undefined) return
    isConsumedRef.current = true
    toast.error('Template not found')
    cleanupParams([TEMPLATE_PARAM])
  }, [templateList, template, templateId, cleanupParams, isConsumedRef])
}

interface TemplateLoadedEffectParams {
  isConsumedRef: MutableRefObject<boolean>
  template: PromptTemplate | undefined
  searchParams: URLSearchParams
  cleanupParams: (paramNames: string[]) => void
  handlers: TemplateActionHandlers
}

const useTemplateLoadedEffect = ({
  isConsumedRef,
  template,
  searchParams,
  cleanupParams,
  handlers,
}: TemplateLoadedEffectParams): void => {
  useEffect(() => {
    if (!template || isConsumedRef.current) return
    isConsumedRef.current = true
    const prefillValues = extractPrefillValues(searchParams)
    cleanupParams([TEMPLATE_PARAM, ...Object.keys(prefillValues)])
    applyTemplateToChat(template, prefillValues, handlers)
  }, [template, searchParams, cleanupParams, handlers, isConsumedRef])
}

export const useChatDeepLink = ({
  agencySlug,
  setInputValue,
  triggerPendingTemplateSend,
}: UseChatDeepLinkParams): UseChatDeepLinkReturn => {
  const [searchParams] = useSearchParams()
  const isConsumedRef = useRef(false)
  const [isPlanRestricted, setIsPlanRestricted] = useState(false)
  const cleanupParams = useCleanupParams()

  const templateId = searchParams.get(TEMPLATE_PARAM)
  const isValidId = templateId !== null && UUID_REGEX.test(templateId)
  const shouldFetch = isValidId && !!agencySlug && !isConsumedRef.current

  const {
    data: templateList,
    error,
    isError,
  } = useGetAgencyPromptTemplatesQuery({ agencySlug: agencySlug ?? '' }, { skip: !shouldFetch })

  const template = useMemo(
    () =>
      templateList && templateId
        ? templateList.itemList.find((t) => t.id === templateId)
        : undefined,
    [templateList, templateId]
  )

  useInvalidIdEffect({ isConsumedRef, templateId, isValidId, cleanupParams })
  useFetchErrorEffect({ isConsumedRef, isError, error, cleanupParams, setIsPlanRestricted })
  useTemplateMissingEffect({ isConsumedRef, templateList, templateId, template, cleanupParams })
  useTemplateLoadedEffect({
    isConsumedRef,
    template,
    searchParams,
    cleanupParams,
    handlers: { setInputValue, triggerPendingTemplateSend },
  })

  return { isPlanRestricted }
}
